import { useState } from "react";

import {
  ApiKeysService,
  type CreateApiKeyRequest,
  type CreateApiKeyResponse,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface ApiKeyCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (created: CreateApiKeyResponse) => void;
}

export function ApiKeyCreateDialog({
  open,
  onClose,
  onSuccess,
}: ApiKeyCreateDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [rateLimit, setRateLimit] = useState("60");
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState("30");

  const { mutate, isLoading, error, reset } = useMutation<
    CreateApiKeyRequest,
    CreateApiKeyResponse
  >((body) => ApiKeysService.create(body), {
    onSuccess: (data) => {
      setName("");
      setDescription("");
      setRateLimit("60");
      setHasExpiry(false);
      setExpiresInDays("30");
      onSuccess(data);
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;
    const body: CreateApiKeyRequest = {
      name: trimmedName,
      rate_limit_per_minute: Math.max(1, Math.min(10_000, Number(rateLimit) || 60)),
    };
    const desc = description.trim();
    if (desc) body.description = desc;
    if (hasExpiry) {
      const days = Math.max(1, Math.min(365, Number(expiresInDays) || 30));
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + days);
      body.expires_at = expiresAt.toISOString();
    }
    await mutate(body);
  };

  return (
    <Dialog open={open} onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("apiKeys.createDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("apiKeys.createDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <FormField
            label={t("apiKeys.createDialog.nameLabel")}
            value={name}
            onChange={setName}
            placeholder={t("apiKeys.createDialog.namePlaceholder")}
            maxLength={255}
            required
            autoFocus
          />
          <FormField
            label={t("apiKeys.createDialog.descriptionLabel")}
            value={description}
            onChange={setDescription}
            placeholder={t("apiKeys.createDialog.descriptionPlaceholder")}
            maxLength={500}
          />
          <FormField
            label={t("apiKeys.createDialog.rateLimitLabel")}
            type="number"
            value={rateLimit}
            onChange={setRateLimit}
            min={1}
            max={10000}
            required
            hint={t("apiKeys.createDialog.rateLimitHint")}
          />

          <div className="rounded-md border border-ocean-100 bg-ocean-50/30 px-3 py-2.5">
            <label className="flex items-center gap-2 text-sm text-ocean-800">
              <input
                type="checkbox"
                checked={hasExpiry}
                onChange={(e) => setHasExpiry(e.target.checked)}
                className="h-4 w-4 rounded border-ocean-200 text-ocean-600 focus:ring-ocean-400"
              />
              <span>{t("apiKeys.createDialog.expiryToggle")}</span>
            </label>
            {hasExpiry && (
              <div className="mt-2 flex items-center gap-2 pl-6">
                <input
                  type="number"
                  value={expiresInDays}
                  min={1}
                  max={365}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                  className="h-9 w-20 rounded-md border border-ocean-100 bg-white px-2 text-sm focus:border-ocean-400 focus:outline-none"
                />
                <span className="text-sm text-neutral-600">
                  {t("apiKeys.createDialog.expiryUnit")}
                </span>
              </div>
            )}
          </div>

          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("apiKeys.createDialog.cancel")}
          </Button>
          <Button type="submit" disabled={isLoading || !name.trim()}>
            {isLoading
              ? t("apiKeys.createDialog.submitting")
              : t("apiKeys.createDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
